import { Canvas } from "@react-three/fiber";

import type { LaneView, LaneViewId } from "../presentation/game-view-model";
import { ContextLifecycle, type WebGlContextStatus } from "./ContextLifecycle";

const LANE_X: Record<LaneViewId, number> = {
  left: -2.05,
  center: 0,
  right: 2.05,
};

/** Preserves the fixture-oriented developer scene while Player Mode owns the physical battlefield. */
export function DeveloperBattlefield3D({
  lanes,
  selectedLaneId,
  legalTargetIds,
  reducedMotion,
  onSelectLane,
  onContextStatusChange,
}: {
  readonly lanes: readonly LaneView[];
  readonly selectedLaneId?: LaneViewId | undefined;
  readonly legalTargetIds: readonly LaneViewId[];
  readonly reducedMotion: boolean;
  readonly onSelectLane: (laneId: LaneViewId) => void;
  readonly onContextStatusChange: (status: WebGlContextStatus) => void;
}) {
  return (
    <Canvas
      camera={{ fov: 44, near: 0.1, far: 100, position: [0, 8.1, 8.8] }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, powerPreference: "high-performance" }}
    >
      <color attach="background" args={["#071019"]} />
      <ambientLight intensity={0.78} />
      <directionalLight color="#ffe4a6" intensity={2.45} position={[4, 8, 5]} />
      <ContextLifecycle onStatusChange={onContextStatusChange} />
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.18, 0]}>
        <boxGeometry args={[8.8, 9.3, 0.18]} />
        <meshStandardMaterial color="#182c33" metalness={0.14} roughness={0.72} />
      </mesh>
      {lanes.map((lane) => {
        const selected = lane.laneId === selectedLaneId;
        const legal = legalTargetIds.includes(lane.laneId);
        return (
          <group key={lane.laneId}>
            <mesh position={[LANE_X[lane.laneId], -0.035, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <boxGeometry args={[1.94, 7.85, 0.075]} />
              <meshStandardMaterial
                color={legal ? "#315f49" : selected ? "#66512d" : "#24373b"}
                transparent
                opacity={legal || selected ? 0.9 : 0.7}
                metalness={0.12}
                roughness={0.78}
              />
            </mesh>
            <mesh
              position={[LANE_X[lane.laneId], 0.02, 0]}
              rotation={[-Math.PI / 2, 0, 0]}
              onClick={(event) => {
                event.stopPropagation();
                onSelectLane(lane.laneId);
              }}
            >
              <planeGeometry args={[1.94, 7.85]} />
              <meshBasicMaterial transparent opacity={0} />
            </mesh>
          </group>
        );
      })}
      {reducedMotion ? null : (
        <pointLight color="#d64e5d" intensity={8} position={[-5, 1.5, 4]} distance={12} />
      )}
    </Canvas>
  );
}
